import React, { useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../Axios';
import Loader from '../global/Loader';
import Toast from '../../services/Toast';
import { isLogedIn, manageLoginData } from '../../services/AuthService';
import { clearPendingApplication, getPendingApplication } from '../../services/PendingApplication';
import { useBrand } from '../../hooks/useBrand';
import TwoFaForm from '../two-fa/TwoFaForm';
import AuthenticatorForm from '../authenticator-form/AuthenticatorForm';
import ProductsTagline from '../products-tagline/ProductsTagline';
// TEMPORARY (see the "old login screen" link below) — remove with it.
import Const from '../../config/const';
import './LoginForm.css';

const LoginForm: React.FC = () => {
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [loginResponse, setLoginResponse] = useState<any>(null);
  // The app to hand off to after signing in. Resolved from storage as well as
  // the URL, because the redirect chain that landed the user here (stale-token
  // bounce, 401 reload) may have stripped ?application= along the way. Held in
  // state so it can be dropped when the server rejects it — the URL alone
  // cannot be un-set, and re-reading it would keep resurrecting a dead target.
  const [application, setApplication] = useState<string | null>(() => getPendingApplication());
  // Whose sign-in page this is. Falls back to the Nextek identity when no
  // product sent the user here, in which case `style` is empty and the card
  // renders exactly as it did before branding existed.
  const { brand, isBranded, style: brandStyle } = useBrand();

  // ══ TEMPORARY: "old login screen" escape hatch — REMOVE AFTER THE SSO
  // ROLLOUT WINDOW. Origin of the linentech app, derived from its
  // /login-from-sso handoff URL so no new env var is needed in any
  // environment. Null (link hidden) if that URL is unset or malformed.
  const oldLoginUrl = React.useMemo(() => {
    try {
      return new URL(String(Const.linentech)).origin + '/old-login';
    } catch {
      return null;
    }
  }, []);

  const handleEmailFocus = () => {
    setIsEmailFocused(true);
  };

  const handleEmailBlur = () => {
    setIsEmailFocused(false);
  };

  const handlePasswordFocus = () => {
    setIsPasswordFocused(true);
  };

  const handlePasswordBlur = () => {
    setIsPasswordFocused(false);
  };

  const handleLogin = async () => {
    try {
      if (isLogedIn()){
        window.location.href = "/home";
        return ;
      }
      if (!username) {
        setIsEmailFocused(true);
        Toast.error('Username or Email is required.');
        return;
      }
      if (!password) {
        setIsPasswordFocused(true);
        Toast.error('Password is required.');
        return;
      }
      setLoading(true);
      const payload = {
        username: username,
        password: password,
        application: application
      };

      // Keep the query the server sees in step with the resolved target: the URL
      // may still name an application that was already rejected, or none at all
      // when the target came from storage.
      const queryParams = Object.fromEntries(searchParams.entries());
      if (application) {
        queryParams.application = application;
      } else {
        delete queryParams.application;
      }

      const response = await axiosInstance.post('/auth/login', payload,{params: queryParams});
      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setLoading(false);
        return;
      }
      
      const userData = response.data.data;
      // If mfa_type exists, MFA is required
      if (userData.mfa_type) {
        setLoginResponse(userData);
        setLoading(false);
        return;
      }
      
      // No mfa_type means direct login (token should exist)
      if (userData.token) {
        manageLoginData(userData, application);
        return;
      }

      // Fallback - shouldn't happen but handle it
      Toast.error('Unexpected response from server.');
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      // Login rejects with 401 both for bad credentials and for correct
      // credentials on an app the user has no access to. In the second case the
      // handoff target can never succeed, so forget it — otherwise every retry
      // in this tab re-sends it and the user can never reach the portal itself.
      // Matched on the server's message because both cases share the status.
      const message: string = error?.response?.data?.message ?? '';
      if (application && message.includes('not authorized to access this application')) {
        clearPendingApplication();
        setApplication(null);
      }
    }
  };
  // Handle MFA flows based on mfa_type
  if (loginResponse) {
    if (loginResponse.mfa_type === 'authenticator') {
      return <AuthenticatorForm loginResponse={loginResponse} />;
    }
    // Default to email OTP (mfa_type === 'email' or legacy flow)
    return <TwoFaForm loginResponse={loginResponse} />;
  }
  return (
    <div className="auth-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="auth-card__inner">
        {/* Decorative elements */}
        <div className="auth-card__decoration">
          <div className="auth-card__circle auth-card__circle--1" />
          <div className="auth-card__circle auth-card__circle--2" />
        </div>

        {/* Logo — the incoming product's lockup, or the Nextek one by default.
            Height is per-brand: the six lockups range from ~2.4:1 to ~4.9:1, so
            one shared height would leave half of them unreadable. */}
        <div className="auth-card__logo">
          <img
            src={brand.logo}
            alt={brand.name}
            style={{ height: `${brand.logoHeight}px` }}
            data-cy="login-brand-logo"
          />
          {/* Nextek is still the account provider, so it stays on the card — as
              the parent of the product, not as the thing being signed into. */}
          {isBranded && (
            <p className="auth-card__parent-brand">
              A product of <strong>Nextek Systems</strong>
            </p>
          )}
        </div>

        {/* Header */}
        <div className="auth-card__header">
          <h1 className="auth-card__title">
            <span className="auth-card__title-accent">Welcome</span> back
          </h1>
          <p className="auth-card__subtitle">Sign in to continue to {brand.name}</p>
        </div>

        {/* ══ TEMPORARY: go-live support notice — REMOVE AFTER THE SSO ROLLOUT
            WINDOW, alongside the "previous sign-in page" block below.
            Sign-in is the one screen where a stuck user has nowhere else to go,
            so the number lives here rather than in the footer. tel: so it dials
            straight from a phone; role="status" (not "alert") because it is
            standing context, not a response to something the user just did. ══ */}
        <div className="auth-notice" role="status">
          <div className="auth-notice__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </div>
          <p className="auth-notice__text">
            We&rsquo;ve moved to a new sign-in. Need help? Call{' '}
            <a href="tel:+18437889298" className="auth-notice__phone" data-cy="login-support-phone">+1 (843) 788-9298</a>
          </p>
        </div>
        {/* ══════════════ END TEMPORARY (go-live support notice) ══════════════ */}

        {/* Form */}
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }} data-cy="login-form">
          <div className={`auth-input-group ${isEmailFocused ? 'auth-input-group--focused' : ''} ${username ? 'auth-input-group--filled' : ''}`}>
            <div className="auth-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M20 21a8 8 0 1 0-16 0"/>
              </svg>
            </div>
            <input
              type="text"
              onChange={(e: any) => setUserName(e.target.value)}
              name="userEmail"
              id="userEmail"
              placeholder="Username"
              className="auth-input"
              value={username}
              onFocus={handleEmailFocus}
              onBlur={handleEmailBlur}
              data-cy="login-username-input"
            />
            <div className="auth-input-highlight" />
          </div>

          <div className={`auth-input-group ${isPasswordFocused ? 'auth-input-group--focused' : ''} ${password ? 'auth-input-group--filled' : ''}`}>
            <div className="auth-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="userPassword"
              onChange={(e: any) => setPassword(e.target.value)}
              id="userPassword"
              placeholder="Password"
              value={password}
              className="auth-input auth-input--password"
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
              data-cy="login-password-input"
            />
            <button
              type="button"
              className="auth-input-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              data-cy="login-toggle-password"
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <path d="m1 1 22 22"/>
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
            <div className="auth-input-highlight" />
          </div>

          <div className="auth-options">
            <NavLink to={`/forgot-password${application ? `?application=${encodeURIComponent(application)}` : ''}`} className="auth-link" data-cy="login-forgot-password-link">Forgot Password?</NavLink>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`auth-btn ${loading ? 'auth-btn--loading' : ''}`}
            data-cy="login-submit-btn"
          >
            {loading ? (
              <div className="auth-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                Sign In
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </form>

        {/* ══ TEMPORARY: "previous sign-in page" escape hatch — REMOVE AFTER THE
            SSO ROLLOUT WINDOW (expected ~2 weeks from Aug 2026).
            For linentech users whose browser-saved password doesn't autofill on
            this new domain — hence the hint copy, which names that symptom
            rather than a vague "trouble signing in". That page still
            authenticates against SSO server-to-server, so it is a familiar
            form, not an SSO bypass. Shown only when the handoff came from
            linentech.
            Deliberately gated on the URL param, NOT the resolved `application`
            state: that value falls back to the sessionStorage copy, which keeps
            the link on screen for the rest of the tab's life once linentech has
            handed off even once — including on the bare portal login. Every
            other use of the application in this file DOES want that fallback,
            so don't sweep this one along with them.
            Styled as an accent-tinted secondary action below a rule:
            prominent enough that a user locked out by autofill actually finds it
            (owner asked for more prominence on 2026-08-07), while the gradient
            fill reserved for .auth-btn keeps Sign In the primary. ══ */}
        {searchParams.get('application') === 'linentech' && oldLoginUrl && (
          <div className="auth-legacy">
            <p className="auth-legacy__hint">Saved password not filling in?</p>
            <a href={oldLoginUrl} className="auth-legacy__btn" data-cy="login-old-login-link">
              Use the old sign-in page
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14"/>
                <path d="m12 5 7 7-7 7"/>
              </svg>
            </a>
          </div>
        )}
        {/* ══════════════ END TEMPORARY (previous sign-in page) ══════════════ */}

        {/* Footer */}
        <div className="auth-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
